/* eslint-disable @typescript-eslint/no-explicit-any */
import type { Middleware, UnknownAction } from '@reduxjs/toolkit';

import { signOut } from '@/redux/auth/actions';

const SIGN_OUT_TYPE = signOut.type;

function getResponseStatusFromAxiosLike(error: unknown): number | undefined {
  if (error === null || error === undefined || typeof error !== 'object') {
    return undefined;
  }

  const status = (error as { response?: { status?: number } }).response?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Extract HTTP status from a Redux action when an async thunk rejected due to an API error.
 *
 * RTK `createAsyncThunk` does not preserve full AxiosError on the action by default.
 * We therefore try, in order:
 * 1. `payload` if it is still an Axios-shaped error (tests / custom rejectWithValue).
 * 2. Axios default English message: "Request failed with status code NNN".
 */
export function getHttpStatusFromAction(
  action: UnknownAction,
): number | undefined {
  const fromPayload = getResponseStatusFromAxiosLike(
    (action as { payload?: unknown }).payload,
  );
  if (fromPayload === 401 || fromPayload === 403) {
    return fromPayload;
  }

  const message = (action as { error?: { message?: string } }).error?.message;
  if (typeof message === 'string') {
    const match = /status code (\d{3})/i.exec(message);
    if (match) {
      return Number.parseInt(match[1], 10);
    }
  }

  return undefined;
}

function isAsyncThunkRejectedAction(action: UnknownAction): boolean {
  return typeof action.type === 'string' && action.type.endsWith('/rejected');
}

function isAbortedRejectedAction(action: UnknownAction): boolean {
  return Boolean((action as { meta?: { aborted?: boolean } }).meta?.aborted);
}

/**
 * Listens to all actions. When an async thunk rejects with HTTP 401 or 403, dispatches `signOut`.
 *
 * Note: axios interceptor still clears tokens on 401/403 for non-thunk calls; this middleware
 * keeps Redux auth state aligned when failures surface as thunk rejections.
 */
export const unauthorizedSessionMiddleware: Middleware =
  store => (next: any) => {
    return (action: any) => {
      const result = next(action);

      const shouldEvaluateUnauthorized =
        action.type !== SIGN_OUT_TYPE &&
        isAsyncThunkRejectedAction(action) &&
        !isAbortedRejectedAction(action);

      if (shouldEvaluateUnauthorized) {
        const status = getHttpStatusFromAction(action);
        if (status === 401 || status === 403) {
          store.dispatch(signOut());
        }
      }

      return result;
    };
  };
